import axios from "axios";

import { reportContractDrift } from "@/shared/lib/telemetry";

/**
 * FE-таксономія помилок (ADR-0010 D1/D2). Гілкуємо UI по `kind` (не по сирому
 * бекендному `code`) — `kind` ізолює FE від дрейфу контракту. Нормативна
 * матриця status(+code)→kind — spec §4.
 */
export type AppErrorKind =
  | "validation"
  | "auth"
  | "forbidden"
  | "notFound"
  | "conflict"
  | "rateLimited"
  | "server"
  | "network"
  | "unknown";

const APP_ERROR_KINDS = new Set<AppErrorKind>([
  "validation",
  "auth",
  "forbidden",
  "notFound",
  "conflict",
  "rateLimited",
  "server",
  "network",
  "unknown",
]);

/**
 * Type-guard `AppError` (ADR-0011 D4). Потрібен для дедуплікації в
 * `unhandledrejection`-пастці: `httpClient` reject-ить `AppError`, який
 * interceptor УЖЕ залогував — повторно як error не логуємо. Структурна перевірка
 * (`kind` із таксономії + `retriable:boolean`) відрізняє від axios-error/Error.
 */
export function isAppError(x: unknown): x is AppError {
  if (typeof x !== "object" || x === null) {
    return false;
  }
  const candidate = x as { kind?: unknown; retriable?: unknown };
  return (
    typeof candidate.kind === "string"
    && APP_ERROR_KINDS.has(candidate.kind as AppErrorKind)
    && typeof candidate.retriable === "boolean"
  );
}

/**
 * Плоский нормалізований тип помилки (ADR-0010 D2). Свідомо НЕ дискримінований
 * union (KISS): `kind`-enum + вичерпний `switch` достатній для MVP, простіша
 * i18n-мапа/fallback/телеметрія.
 */
export interface AppError {
  kind: AppErrorKind;
  /** HTTP-статус; відсутній на network/timeout. */
  status?: number;
  /** Сирий бекендний envelope-`code` — для логів/override/телеметрії. */
  code?: string;
  /** Чи має сенс повтор тим самим запитом (network/server/rateLimited = true). */
  retriable: boolean;
  /** Оригінальний axios-error / Error — для dev-логу, НЕ для UI. */
  raw?: unknown;
}

/** Класифікація за відомим HTTP-статусом (spec §4). `null` → статус поза матрицею. */
function classifyByStatus(
  status: number,
): { kind: AppErrorKind; retriable: boolean } | null {
  switch (status) {
    case 400:
    case 422:
      return { kind: "validation", retriable: false };
    case 401:
      return { kind: "auth", retriable: false };
    case 403:
      return { kind: "forbidden", retriable: false };
    case 404:
      return { kind: "notFound", retriable: false };
    case 409:
      return { kind: "conflict", retriable: false };
    case 413:
    case 415:
      return { kind: "server", retriable: false };
    case 429:
      return { kind: "rateLimited", retriable: true };
    case 500:
    case 503:
      return { kind: "server", retriable: true };
    default:
      return null;
  }
}

/**
 * Чиста нормалізація `axios-error | network | timeout | будь-що → AppError`
 * (ADR-0010 D3). **Ніколи не кидає.** Без router/store/i18n/мережі — лише
 * класифікація, тестабельна по кожній гілці.
 *
 * - не-axios → `unknown`;
 * - axios без `response` (offline/timeout `ECONNABORTED`/DNS) → `network` (retriable);
 * - axios з `response` → `kind` за статусом (spec §4); `code` тягнемо лише якщо рядок;
 * - відсутній/нерозпарсений envelope (немає рядкового `code`) або невідомий статус
 *   → fallback по статус-класу + `reportContractDrift` (D8).
 */
export function normalizeError(err: unknown): AppError {
  // Ідемпотентність: httpClient interceptor вже нормалізує → повертаємо як є.
  if (isAppError(err)) {
    return err;
  }

  if (!axios.isAxiosError(err)) {
    return { kind: "unknown", retriable: false, raw: err };
  }

  if (axios.isCancel(err)) {
    // скасований запит (AbortController → CanceledError) — не помилка показу,
    // не «сервер недоступний». unknown без retry: не тостимо/не алертимо.
    return { kind: "unknown", retriable: false, raw: err };
  }

  if (!err.response) {
    // немає response: offline / timeout (ECONNABORTED) / DNS
    return { kind: "network", retriable: true, raw: err };
  }

  const status = err.response.status;
  const data = err.response.data as { code?: unknown } | null | undefined;
  const code = typeof data?.code === "string" ? data.code : undefined;

  const byStatus = classifyByStatus(status);
  if (byStatus) {
    // Відомий статус, але envelope без рядкового `code` → дрейф контракту (D8),
    // класифікація лишається за статусом.
    if (code === undefined) {
      reportContractDrift({ status, code, raw: err });
    }
    return { kind: byStatus.kind, status, code, retriable: byStatus.retriable, raw: err };
  }

  // Невідомий статус → fallback по статус-класу + дрейф (D8).
  reportContractDrift({ status, code, raw: err });
  if (status >= 500) {
    return { kind: "server", status, code, retriable: true, raw: err };
  }
  return { kind: "unknown", status, code, retriable: false, raw: err };
}
